'use strict';
import { useMemo } from 'react';
import { SelectionState, ICrossReferencedGrower } from './DataTransferTroubleshootingTool';

/**
 * Determine which growers match from source to dest by foundation Id, and why they can or cannot be transfered
 * @param source The source grower state to copy from
 * @param dest The destination grower state to copy to
 */
export function useViableCrossReferences(source: SelectionState, dest: SelectionState) 
{
	// Map all source growers to dest growers by foundation id
	const potentialPairings = useMemo(() => 
	{
		return source.growers
			?.map(sg => ([sg.Id, dest.growers?.find(dg => sg.FoundationId == dg.FoundationId)] as const))
			.filter(pair => pair[1]) ?? [];
	}, [source.growers, dest.growers]);

	// Validate each potential pairing to see if it can be transfered
	const crossReferencePairings: Record<string, ICrossReferencedGrower> = useMemo(() => 
	{
		if (!potentialPairings?.length) 
		{
			return undefined;
		}

		const crossReference = Object.fromEntries(potentialPairings.map(([sourceGrowerId, destGrower]) => 
		{
			const foundationId = destGrower.FoundationId;
			const sourceGrower = source.growers?.find(sg => sg.Id === sourceGrowerId);
			// Make sure there are any farms to transfer on the source
			if (!sourceGrower?.Farms?.length) 
			{
				return [foundationId, { sourceGrowerId, destGrowerId: destGrower.Id, foundationId, valid: false, reason: 'No farms in source grower' }];
			}

			// Make sure that there are not duplicate farm names in the destination
			const dupe = sourceGrower?.Farms?.find(sfm => destGrower?.Farms?.some(dfm => sfm.Name === dfm.Name));
			if (dupe) 
			{
				return [foundationId, { sourceGrowerId, destGrowerId: destGrower.Id, foundationId, valid: false, reason: `Account Farm '${dupe.Name}' exists in the destination already.` }];
			}

			// This is viable
			return [foundationId, { sourceGrowerId, destGrowerId: destGrower.Id, foundationId, valid: true, reason: '' }];
		}));

		return crossReference;
	}, [potentialPairings, source.growers]);
	return { potentialPairings, crossReferencePairings };
}
